import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface UiState {
  // États UI généraux
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'auto';
  language: string;

  // États spécifiques dashboard
  dashboardFilters: Record<string, unknown>;
  selectedTab: string;
  modalOpen: boolean;
  modalType: string | null;

  // États performance
  loadingStates: Record<string, boolean>;
  errorStates: Record<string, string | null>;

  // États responsive
  screenSize: 'mobile' | 'tablet' | 'desktop';
  isOnline: boolean;

  // États performance
  performanceMode: boolean; // true = Lite Mode (Optimisé pour Android 10/2Go)
}

interface UiActions {
  // Actions générales
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: UiState['theme']) => void;
  setLanguage: (language: string) => void;

  // Actions dashboard
  setDashboardFilters: (filters: Record<string, unknown>) => void;
  setSelectedTab: (tab: string) => void;
  setModalOpen: (open: boolean, type?: string | null) => void;

  // Actions performance
  setLoadingState: (key: string, loading: boolean) => void;
  setErrorState: (key: string, error: string | null) => void;

  // Actions responsive
  setScreenSize: (size: UiState['screenSize']) => void;
  setOnlineStatus: (online: boolean) => void;

  // Actions performance
  setPerformanceMode: (mode: boolean) => void;

  // Actions utilitaires
  resetUiState: () => void;
}

const initialState: UiState = {
  sidebarCollapsed: false,
  theme: 'auto',
  language: 'fr',
  dashboardFilters: {},
  selectedTab: 'overview',
  modalOpen: false,
  modalType: null,
  loadingStates: {},
  errorStates: {},
  screenSize: 'desktop',
  isOnline: typeof navigator !== 'undefined' ? (navigator.onLine ?? true) : true,
  performanceMode: false,
};

export const useUiStore = create<UiState & UiActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Actions générales
        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
        setTheme: (theme) => set({ theme }),
        setLanguage: (language) => set({ language }),

        // Actions dashboard
        setDashboardFilters: (filters) => set({ dashboardFilters: filters }),
        setSelectedTab: (tab) => set({ selectedTab: tab }),
        setModalOpen: (open, type = null) => set({ modalOpen: open, modalType: type }),

        // Actions performance
        setLoadingState: (key, loading) => set((state) => ({
          loadingStates: { ...state.loadingStates, [key]: loading }
        })),
        setErrorState: (key, error) => set((state) => ({
          errorStates: { ...state.errorStates, [key]: error }
        })),

        // Actions responsive
        setScreenSize: (size) => set({ screenSize: size }),
        setOnlineStatus: (online) => set({ isOnline: online }),

        setPerformanceMode: (mode) => set({ performanceMode: mode }),

        // Actions utilitaires
        resetUiState: () => set(initialState),
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
          language: state.language,
          dashboardFilters: state.dashboardFilters,
          selectedTab: state.selectedTab,
          performanceMode: state.performanceMode,
        }),
      }
    ),
    {
      name: 'ui-store',
    }
  )
);

// Selectors optimisés pour éviter les re-renders inutiles
export const useSidebarCollapsed = () => useUiStore((state) => state.sidebarCollapsed);
export const useTheme = () => useUiStore((state) => state.theme);
export const useLanguage = () => useUiStore((state) => state.language);
export const useDashboardFilters = () => useUiStore((state) => state.dashboardFilters);
export const useSelectedTab = () => useUiStore((state) => state.selectedTab);
export const useModalState = () => useUiStore((state) => ({ modalOpen: state.modalOpen, modalType: state.modalType }));
export const useLoadingState = (key: string) => useUiStore((state) => state.loadingStates[key] ?? false);
export const useErrorState = (key: string) => useUiStore((state) => state.errorStates[key] ?? null);
export const useScreenSize = () => useUiStore((state) => state.screenSize);
export const useOnlineStatus = () => useUiStore((state) => state.isOnline);
export const usePerformanceMode = () => useUiStore((state) => state.performanceMode);
export const useSetPerformanceMode = () => useUiStore((state) => state.setPerformanceMode);
